import React from "react";
import styled from "styled-components";
import { useTranslate } from "@tolgee/react";
import { ReactComponent as RadioIcon } from "../../Assets/iconsv2/generic/radioButtonIcon.svg";
import { ReactComponent as MultipleChoiceIcon } from "../../Assets/iconsv2/generic/checkBoxIcon.svg";
import { ANSWER_TYPE_SINGLE, ANSWER_TYPE_MULTIPLE } from "../../Constants/constantValues";

const PollResultsDisplay = ({
  poll,
  variant = "organizer",
  showVoteLabel = false
}) => {
  const { t } = useTranslate();

  const calculatePercentage = (optionId) => {
    const voteCount = poll.results[optionId] || 0;
    return poll.total_responses > 0 
      ? Math.round((voteCount / poll.total_responses) * 100) 
      : 0;
  };

  const isUserChoice = (optionId) => {
    return poll.user_response?.selected_option_ids?.includes(optionId);
  };

  if (variant === "compact") {
    return (
      <>
        {poll.options.map((option) => {
          const voteCount = poll.results[option.id] || 0;
          const percentage = calculatePercentage(option.id);

          return (
            <CompactResultRow key={option.id}>
              <CompactResultTop>
                <OptionIcon $type={poll.answer_type}>
                  {poll.answer_type === ANSWER_TYPE_SINGLE ? (
                    <RadioIcon width={18} height={18} />
                  ) : (
                    <MultipleChoiceIcon width={18} height={18} />
                  )}
                </OptionIcon>
                <CompactOptionText>{option.option_text}</CompactOptionText>
                <CompactStats>
                  <CompactVoteCount>{voteCount}</CompactVoteCount>
                  <CompactPercentage>{percentage}%</CompactPercentage>
                </CompactStats>
              </CompactResultTop>
              <CompactProgressBar>
                <CompactProgressFill $percentage={percentage} />
              </CompactProgressBar>
            </CompactResultRow>
          );
        })}
      </>
    );
  }

  return (
    <ResultsContainer>
      {showVoteLabel && (
        <ResultsLabelTop>
          {poll.total_responses} {poll.total_responses === 1 ? t("conferences.vote") : t("conferences.votes")}
        </ResultsLabelTop>
      )}
      {poll.options.map((option) => {
        const voteCount = poll.results[option.id] || 0;
        const percentage = calculatePercentage(option.id);
        const userChoice = isUserChoice(option.id);

        return (
          <ResultOption key={option.id} $isUserChoice={userChoice}>
            <ResultOptionTop>
              {poll.answer_type === ANSWER_TYPE_SINGLE ? (
                <RadioButton $selected={userChoice}>
                  {userChoice && <RadioDot />}
                </RadioButton>
              ) : (
                <CheckboxButton $selected={userChoice}>
                  {userChoice && <CheckIcon>✓</CheckIcon>}
                </CheckboxButton>
              )}
              <ResultOptionLabel>{option.option_text}</ResultOptionLabel>
              <ResultStats>
                <VoteCountSmall>{voteCount}</VoteCountSmall>
                <PercentageSmall>{percentage}%</PercentageSmall>
              </ResultStats>
            </ResultOptionTop>
            <ProgressBar>
              <ProgressFill $percentage={percentage} $isUserChoice={userChoice} />
            </ProgressBar>
          </ResultOption>
        );
      })}
    </ResultsContainer>
  );
};

export default PollResultsDisplay;

const CompactResultRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  background: ${({ theme }) => theme.menu.groupContentBackground};
  border: 1px solid ${({ theme }) => theme.sidebar.borderColor};
`;

const CompactResultTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const OptionIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  line-height: 1;
  min-width: 22px;
  text-align: center;
  
  svg {
    fill: ${({ theme }) => theme.menu.secondaryTextColor};
  }
`;

const CompactOptionText = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  flex: 1;
  word-break: break-word;
`;

const CompactStats = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const CompactVoteCount = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  min-width: 20px;
  text-align: right;
`;

const CompactPercentage = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.menu.selectedTextColor};
  min-width: 42px;
  text-align: right;
`;

const CompactProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${({ theme }) => theme.menu.background};
  border-radius: 3px;
  overflow: hidden;
`;

const CompactProgressFill = styled.div`
  height: 100%;
  width: ${({ $percentage }) => $percentage}%;
  background: ${({ theme }) => theme.menu.selectedTextColor};
  opacity: 0.7;
  border-radius: 3px;
  transition: width 0.4s ease;
`;

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ResultsLabelTop = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: ${({ theme }) => theme.menu.secondaryTextColor};
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px solid ${({ theme }) => theme.sidebar.borderColor};
`;

const ResultOption = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 10px;
  background: ${({ theme, $isUserChoice }) =>
    $isUserChoice ? `${theme.menu.selectedTextColor}10` : theme.menu.groupContentBackground};
  border: 2px solid ${({ theme, $isUserChoice }) =>
    $isUserChoice ? theme.menu.selectedTextColor : theme.sidebar.borderColor};
`;

const ResultOptionTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RadioButton = styled.div`
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  border: 2px solid ${({ theme, $selected }) => 
    $selected ? theme.menu.selectedTextColor : theme.sidebar.borderColor};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme }) => theme.menu.background};
  transition: all 0.2s ease;
`;

const RadioDot = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${({ theme }) => theme.menu.selectedTextColor};
`;

const CheckboxButton = styled.div`
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 4px;
  border: 2px solid ${({ theme, $selected }) => 
    $selected ? theme.menu.selectedTextColor : theme.sidebar.borderColor};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ theme, $selected }) => 
    $selected ? theme.menu.selectedTextColor : theme.menu.background};
  transition: all 0.2s ease;
`;

const CheckIcon = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #FFFFFF;
  line-height: 1;
`;

const ResultOptionLabel = styled.span`
  font-size: 15px;
  font-weight: 500;
  color: ${({ theme }) => theme.menu.primaryTextColor};
  flex: 1;
`;

const ResultStats = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
`;

const VoteCountSmall = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.menu.primaryTextColor};
`;

const PercentageSmall = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.menu.selectedTextColor};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: ${({ theme }) => theme.menu.background};
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${({ $percentage }) => $percentage}%;
  background: ${({ theme, $isUserChoice }) =>
    $isUserChoice ? theme.menu.selectedTextColor : theme.menu.secondaryTextColor};
  opacity: ${({ $isUserChoice }) => ($isUserChoice ? 0.9 : 0.6)};
  border-radius: 3px;
  transition: width 0.4s ease;
`;